import React from 'react';
import { Target, Percent, ArrowDownRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

export function AlertRuleFormDialog({
  showCreateModal,
  editingRule,
  setShowCreateModal,
  setEditingRule,
  resetForm,
  formData,
  setFormData,
  handleCreateRule,
  handleUpdateRule
}) {
  return (
    <Dialog 
      open={showCreateModal || editingRule !== null} 
      onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setEditingRule(null);
          resetForm();
        }
      }}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
          </DialogTitle>
          <DialogDescription>
            Configure when you want to be notified about cohort score changes.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Rule Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Critical LEI Drop Alert"
              data-testid="rule-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this alert"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Alert Type</Label>
            <Select
              value={formData.alert_type}
              onValueChange={(v) => setFormData({ ...formData, alert_type: v })}
            >
              <SelectTrigger data-testid="alert-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="threshold_breach">
                  <div className="flex items-center gap-2">
                    <Target size={16} />
                    Score Below Threshold
                  </div>
                </SelectItem>
                <SelectItem value="decline_percent">
                  <div className="flex items-center gap-2">
                    <Percent size={16} />
                    Percentage Decline
                  </div>
                </SelectItem>
                <SelectItem value="decline_points">
                  <div className="flex items-center gap-2">
                    <ArrowDownRight size={16} />
                    Point Drop
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Metric to Monitor</Label>
            <Select
              value={formData.metric_type}
              onValueChange={(v) => setFormData({ ...formData, metric_type: v })}
            >
              <SelectTrigger data-testid="metric-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">LEI & KPI (Both)</SelectItem>
                <SelectItem value="lei">LEI Only</SelectItem>
                <SelectItem value="kpi">KPI Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="threshold">
              Threshold Value
              {formData.alert_type === 'threshold_breach' && ' (alert if score drops below this)'}
              {formData.alert_type === 'decline_percent' && ' (alert if score declines by this %)'}
              {formData.alert_type === 'decline_points' && ' (alert if score drops by this many points)'}
            </Label>
            <Input
              id="threshold"
              type="number"
              value={formData.threshold_value}
              onChange={(e) => setFormData({ ...formData, threshold_value: parseFloat(e.target.value) })}
              min={0}
              max={formData.alert_type === 'decline_percent' ? 100 : 100}
              data-testid="threshold-input"
            />
          </div>
          
          {/* Schedule Configuration */}
          <div className="pt-4 border-t space-y-4">
            <h4 className="font-medium text-gray-900">Automated Monitoring</h4>
            
            <div className="space-y-2">
              <Label>Check Frequency</Label>
              <Select
                value={formData.schedule_frequency}
                onValueChange={(v) => setFormData({ ...formData, schedule_frequency: v })}
              >
                <SelectTrigger data-testid="schedule-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Only</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {formData.schedule_frequency === 'manual' 
                  ? 'Only check when you click "Check Now"'
                  : `Automatically check ${formData.schedule_frequency} and notify if alerts trigger`
                }
              </p>
            </div>
            
            {formData.schedule_frequency !== 'manual' && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email_notifications">Email Notifications</Label>
                    <p className="text-xs text-gray-500">Get email when alerts trigger</p>
                  </div>
                  <Switch
                    id="email_notifications"
                    checked={formData.email_notifications}
                    onCheckedChange={(v) => setFormData({ ...formData, email_notifications: v })}
                    data-testid="email-notifications-switch"
                  />
                </div>
                
                {formData.email_notifications && (
                  <div className="space-y-2">
                    <Label htmlFor="notification_emails">Notification Emails</Label>
                    <Input
                      id="notification_emails"
                      value={formData.notification_emails}
                      onChange={(e) => setFormData({ ...formData, notification_emails: e.target.value })}
                      placeholder="email1@example.com, email2@example.com"
                      data-testid="notification-emails-input"
                    />
                    <p className="text-xs text-gray-500">Comma-separated email addresses</p>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="enabled">Enable Rule</Label>
            <Switch
              id="enabled"
              checked={formData.enabled}
              onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
              data-testid="enable-switch"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowCreateModal(false);
              setEditingRule(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={editingRule ? handleUpdateRule : handleCreateRule}
            disabled={!formData.name}
            data-testid="save-rule-btn"
          >
            {editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
